import i18n from '@/utils/i18n';
import OrderGuideStarIcon from '@assets/icons/order-guide-star.svg';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatDate } from '@/utils/date';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { Skeleton } from 'react-native-skeletons';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { View } from 'react-native';

interface Props {
  variantPackMetadata: ItemListOptionMetadata;
}

export const InboundDetailOrderInfo: React.FC<Props> = ({
  variantPackMetadata,
}) => {
  const [styles, theme] = useStyles();
  const { isInOrderGuide, loading } = useOrderGuideItemsContext();
  const { lastPurchaseInfoMap } = useVariantPackInfoContext();

  if (loading) {
    return (
      <View style={styles.base}>
        <View style={[styles.cell, styles.cellBorder]}>
          <Skeleton borderRadius={16} height={16} width={16} />

          <View style={styles.skeletonSpacing}>
            <Skeleton height={12} width={20} />
          </View>
        </View>
      </View>
    );
  }

  const vpIsInOrderGuide = isInOrderGuide(variantPackMetadata.variantPackUUID);
  const orderGuideIconColor = vpIsInOrderGuide
    ? theme.sk.color.icon.positive.secondary
    : theme.sk.color.icon.warning.secondary;

  const lastPurchaseInfo =
    lastPurchaseInfoMap[variantPackMetadata.variantPackUUID];
  const metricDate =
    lastPurchaseInfo?.fulfillmentDate ?? lastPurchaseInfo?.orderDate;
  const lastOrderedValue = metricDate
    ? `${lastPurchaseInfo?.quantity ?? '--'} • ${formatDate(metricDate)}`
    : '--';

  return (
    <View style={styles.base}>
      <View style={[styles.cell, styles.cellBorder, styles.orderGuideCell]}>
        <OrderGuideStarIcon stroke={orderGuideIconColor} />

        <Typography color={theme.sk.color.text.primary} variant="paragraph-sm">
          {vpIsInOrderGuide ? i18n.t('yes') : i18n.t('no')}
        </Typography>
      </View>

      {(variantPackMetadata.variantPackOrderCounts || []).map((item) => (
        <View key={item.days} style={[styles.cell, styles.cellBorder]}>
          <View style={styles.cellLabelValue}>
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-xs"
            >
              {`L${item.days}`}
            </Typography>

            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-sm"
            >
              {item.order_count ?? '--'}
            </Typography>
          </View>
        </View>
      ))}

      <View style={styles.cell}>
        <View style={styles.cellLabelValue}>
          <Typography
            color={theme.sk.color.text.tertiary}
            variant="paragraph-xs"
          >
            {i18n.t('last_ordered')}
          </Typography>

          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-sm"
          >
            {lastOrderedValue}
          </Typography>
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'stretch',
    alignSelf: 'stretch',
    borderColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    flexDirection: 'row',
  },
  cell: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  cellBorder: {
    borderColor: theme.sk.color.border.primary,
    borderRightWidth: 1,
  },
  cellLabelValue: {
    alignItems: 'center',
  },
  orderGuideCell: {
    gap: 4,
  },
  skeletonSpacing: {
    marginLeft: 8,
  },
}));
